import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Fade,
  IconButton,
  Stack,
  Tooltip,
  Typography,
  alpha,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import HistoryIcon from "@mui/icons-material/History";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import dayjs from "dayjs";
import {
  getCoComments,
  resolveCoComment,
  unresolveCoComment,
} from "../../api/coReviewApi";
import {
  formatCommentAuthor,
  groupCommentsByThread,
} from "../../api/coReviewCommentAdapter";
import { getIncidentData, getReportMetadata } from "../../api/reportApi";
import type { CoReviewComment } from "../../shared/interfaces/CoReviewComment";
import { ReportStatus } from "../../shared/interfaces/ReportStatus";
import { getUserName } from "../../api/userDirectory";
import { NarrativeWithInlineComments } from "./NarrativeWithInlineComments";

interface Props {
  open: boolean;
  reportId: string;
  onClose: () => void;
}

interface CycleGroup {
  cycle: number;
  rejectionComments: CoReviewComment[];
  rejectedAt?: string;
}

interface CurrentState {
  reportStatus: string;
  reviewedBy?: string | null;
  reviewedByName?: string | null;
  updatedAt?: string;
  reportOwnerUserId?: string;
}

function commentTypeLabel(c: CoReviewComment): string {
  if (c.isSectionRejection) return "Section rejection";
  if (c.narrativeIndex != null) return "Narrative inline";
  return "General";
}

function commentAnchorLabel(c: CoReviewComment): string {
  if (c.fieldLabel) return c.fieldLabel;
  if (c.narrativeIndex != null) return `Narrative #${c.narrativeIndex + 1}`;
  if (c.sectionPath && c.sectionPath !== "$") {
    return c.sectionPath.replace(/^\$\./, "");
  }
  return "General";
}

function statusLabel(status: string): string {
  switch (status) {
    case ReportStatus.IN_CO_REVIEW:
      return "Currently in CO review";
    case ReportStatus.CO_APPROVED:
      return "CO approved";
    case ReportStatus.CHANGES_REQUESTED:
      return "Changes requested";
    case ReportStatus.RMS_COMMITTED:
      return "Committed to RMS";
    case ReportStatus.SIGNED:
      return "Signed (legacy flow)";
    case ReportStatus.PENDING_REVIEW:
      return "Pending review";
    default:
      return status;
  }
}

export function ReviewHistoryDialog({ open, reportId, onClose }: Props) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const [comments, setComments] = useState<CoReviewComment[]>([]);
  const [current, setCurrent] = useState<CurrentState | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // narrativesByVersion[v][i] = narrative text at versionNumber v, index i.
  // Each cycle's narrative comments are rendered against the artifact as it
  // looked when those comments were written, so character anchors line up.
  const [narrativesByVersion, setNarrativesByVersion] = useState<
    Record<number, string[]>
  >({});

  useEffect(() => {
    if (!open || !reportId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    Promise.all([getCoComments(reportId), getReportMetadata(reportId)])
      .then(([c, m]) => {
        if (cancelled) return;
        setComments(c);
        setCurrent({
          reportStatus: m.reportStatus,
          reviewedBy: m.reviewedBy,
          reviewedByName: m.reviewedByName,
          updatedAt: m.updatedAt,
          reportOwnerUserId: m.userId,
        });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const message =
          err && typeof err === "object" && "message" in err
            ? String((err as { message: string }).message)
            : "Failed to load review history.";
        setError(message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, reportId]);

  // Pull each distinct artifact version referenced by the comments so we can
  // render the narrative-as-it-was per cycle. Best-effort: a missing version
  // (e.g. legacy comment with no matching artifact) just yields no narrative
  // text and the inline view falls back to a plain card list.
  useEffect(() => {
    if (!open || !reportId || comments.length === 0) return;
    let cancelled = false;
    const wanted = Array.from(
      new Set(
        comments
          .map((c) => c.reportVersionNumber)
          .filter((v): v is number => typeof v === "number"),
      ),
    ).filter((v) => !(v in narrativesByVersion));
    if (wanted.length === 0) return;
    Promise.allSettled(
      wanted.map((v) => getIncidentData(reportId, v).then((d) => ({ v, d }))),
    ).then((results) => {
      if (cancelled) return;
      const next: Record<number, string[]> = {};
      for (const r of results) {
        if (r.status !== "fulfilled") continue;
        const { v, d } = r.value as {
          v: number;
          d: { reportContent?: Record<string, unknown> };
        };
        const narratives = d?.reportContent?.["Narratives"];
        if (!Array.isArray(narratives)) continue;
        next[v] = narratives.map((entry) => {
          if (!entry || typeof entry !== "object") return "";
          const e = entry as Record<string, unknown>;
          if (typeof e.Text === "string") return e.Text;
          if (typeof e.Narrative === "string") return e.Narrative;
          return "";
        });
      }
      if (Object.keys(next).length > 0) {
        setNarrativesByVersion((prev) => ({ ...prev, ...next }));
      }
    });
    return () => {
      cancelled = true;
    };
  }, [open, reportId, comments, narrativesByVersion]);

  const groups: CycleGroup[] = useMemo(() => {
    const byCycle = new Map<number, CycleGroup>();
    for (const c of comments) {
      let g = byCycle.get(c.reviewCycle);
      if (!g) {
        g = { cycle: c.reviewCycle, rejectionComments: [] };
        byCycle.set(c.reviewCycle, g);
      }
      g.rejectionComments.push(c);
      if (!g.rejectedAt || c.createdAt > g.rejectedAt) {
        g.rejectedAt = c.createdAt;
      }
    }
    return Array.from(byCycle.values()).sort((a, b) => b.cycle - a.cycle);
  }, [comments]);

  // Prefer the server-snapshotted name so officers (no users:read perm) see
  // the reviewer's real name instead of the raw WorkOS user id. Fall back to
  // the directory resolver for legacy rows written before reviewedByName
  // existed, and finally to the raw id.
  const reviewerName =
    current?.reviewedByName ||
    (current?.reviewedBy && getUserName(current.reviewedBy)) ||
    current?.reviewedBy ||
    undefined;

  // Backend allows PUT /comment/{id} resolved only in these two states.
  const canResolve =
    current?.reportStatus === ReportStatus.IN_CO_REVIEW ||
    current?.reportStatus === ReportStatus.CHANGES_REQUESTED;

  const handleResolveToggle = async (commentId: string, nextResolved: boolean) => {
    try {
      if (nextResolved) {
        await resolveCoComment(reportId, commentId);
      } else {
        await unresolveCoComment(reportId, commentId);
      }
      setComments((prev) =>
        prev.map((c) =>
          c.commentId === commentId
            ? {
                ...c,
                resolvedAt: nextResolved ? new Date().toISOString() : null,
                resolvedByUserId: nextResolved ? c.resolvedByUserId ?? null : null,
              }
            : c,
        ),
      );
    } catch (err) {
      const e = err as { response?: { data?: { error?: string } }; message?: string };
      setError(e?.response?.data?.error || e?.message || "Failed to update comment.");
    }
  };

  const isEmpty = !loading && groups.length === 0 && !current;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      fullScreen={fullScreen}
      TransitionComponent={Fade}
      transitionDuration={200}
      PaperProps={{
        elevation: 0,
        sx: {
          borderRadius: fullScreen ? 0 : 3,
          border: "1px solid",
          borderColor: "divider",
        },
      }}
      slotProps={{
        backdrop: {
          sx: {
            backgroundColor: "rgba(0, 0, 0, 0.6)",
            backdropFilter: "blur(4px)",
          },
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          fontWeight: 600,
          pr: 6,
        }}
      >
        <HistoryIcon color="action" />
        Review history
        <IconButton
          onClick={onClose}
          size="small"
          sx={{
            position: "absolute",
            right: 12,
            top: 12,
            color: "text.secondary",
            "&:hover": { backgroundColor: "action.hover" },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {loading && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress size={32} />
          </Box>
        )}
        {error && !loading && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {!loading && !error && (
          <>
            {current && (
              <Box
                sx={{
                  p: 1.5,
                  mb: 2,
                  borderRadius: 1.5,
                  border: `1px solid ${theme.palette.divider}`,
                  bgcolor: alpha(theme.palette.info.main, 0.04),
                }}
              >
                <Stack direction="row" alignItems="center" spacing={1}>
                  <CheckCircleOutlineIcon fontSize="small" color="info" />
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {statusLabel(current.reportStatus)}
                  </Typography>
                  {current.updatedAt && (
                    <Typography variant="caption" color="text.secondary">
                      · {dayjs(current.updatedAt).format("MMM D, h:mm A")}
                    </Typography>
                  )}
                </Stack>
                {reviewerName && (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
                    Last reviewed by {reviewerName}
                  </Typography>
                )}
              </Box>
            )}
            {isEmpty && (
              <Box sx={{ py: 4, textAlign: "center" }}>
                <Typography variant="body2" color="text.secondary">
                  No review activity yet.
                </Typography>
              </Box>
            )}
            <Stack spacing={3}>
              {groups.map((g) => (
                <Box key={g.cycle}>
                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={1}
                    sx={{ mb: 1 }}
                  >
                    <Typography
                      variant="overline"
                      sx={{ fontWeight: 700, letterSpacing: 1 }}
                    >
                      Cycle {g.cycle}
                    </Typography>
                    <Divider sx={{ flexGrow: 1 }} />
                  </Stack>
                  <RejectionEvent
                    when={g.rejectedAt}
                    comments={g.rejectionComments}
                    reportOwnerUserId={current?.reportOwnerUserId}
                    canResolve={canResolve}
                    onToggleResolved={handleResolveToggle}
                    narrativesByVersion={narrativesByVersion}
                  />
                </Box>
              ))}
            </Stack>
            {(groups.length > 0 || current) && (
              <Alert severity="info" variant="outlined" sx={{ mt: 3 }}>
                Cycle-by-cycle status transitions (officer signed,
                resubmitted, approved, RMS-committed) are not yet recorded
                server-side. Only CO comment activity and the current state
                are shown above.
              </Alert>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

function RejectionEvent({
  when,
  comments,
  reportOwnerUserId,
  canResolve,
  onToggleResolved,
  narrativesByVersion,
}: {
  when?: string;
  comments: CoReviewComment[];
  reportOwnerUserId?: string;
  canResolve: boolean;
  onToggleResolved: (commentId: string, nextResolved: boolean) => void;
  narrativesByVersion: Record<number, string[]>;
}) {
  const theme = useTheme();
  // Bucket by parent classification — replies inherit the parent's bucket so
  // each thread renders together regardless of how the reply itself classifies.
  const threads = groupCommentsByThread(comments);
  const sectionRej = threads.filter(({ parent }) => parent.isSectionRejection);
  const narrative = threads.filter(
    ({ parent }) =>
      !parent.isSectionRejection && parent.narrativeIndex != null,
  );
  const general = threads.filter(
    ({ parent }) =>
      !parent.isSectionRejection && parent.narrativeIndex == null,
  );

  // Group narrative threads by their (versionNumber, narrativeIndex) so each
  // narrative renders once with its anchored inline comments — anchors line up
  // against the artifact-at-that-version (falling back to current if the version
  // didn't resolve).
  const narrativeByVersionAndIndex = new Map<
    string,
    {
      versionNumber: number;
      narrativeIndex: number;
      comments: CoReviewComment[];
    }
  >();
  for (const t of narrative) {
    const idx = t.parent.narrativeIndex;
    const ver = t.parent.reportVersionNumber;
    if (typeof idx !== "number" || typeof ver !== "number") continue;
    const key = `${ver}:${idx}`;
    const entry = narrativeByVersionAndIndex.get(key);
    const flat = [t.parent, ...t.replies];
    if (entry) {
      entry.comments.push(...flat);
    } else {
      narrativeByVersionAndIndex.set(key, {
        versionNumber: ver,
        narrativeIndex: idx,
        comments: [...flat],
      });
    }
  }

  return (
    <Box
      sx={{
        p: 1.5,
        borderRadius: 1.5,
        border: `1px solid ${theme.palette.divider}`,
        bgcolor: alpha(theme.palette.background.default, 0.5),
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center">
        <HighlightOffIcon fontSize="small" color="error" />
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          CO requested changes
        </Typography>
        {when && (
          <Typography variant="caption" color="text.secondary">
            · {dayjs(when).format("MMM D, h:mm A")}
          </Typography>
        )}
      </Stack>
      <Stack spacing={1.25} sx={{ mt: 1.25, pl: 3.5 }}>
        {sectionRej.length > 0 && (
          <CommentBucket
            label="Section rejections"
            threads={sectionRej}
            reportOwnerUserId={reportOwnerUserId}
            canResolve={canResolve}
            onToggleResolved={onToggleResolved}
          />
        )}
        {narrativeByVersionAndIndex.size > 0 && (
          <Box>
            <Typography
              variant="caption"
              sx={{ fontWeight: 700, color: "text.secondary" }}
            >
              Narrative inline ({narrative.length})
            </Typography>
            <Stack spacing={1.25} sx={{ mt: 0.5 }}>
              {Array.from(narrativeByVersionAndIndex.values()).map(
                ({ versionNumber, narrativeIndex, comments: items }) => {
                  const text =
                    narrativesByVersion[versionNumber]?.[narrativeIndex] ?? "";
                  if (!text) {
                    // No narrative text resolved (e.g. version fetch failed) —
                    // fall back to a plain card list so the comments still show.
                    return (
                      <CommentBucket
                        key={`${versionNumber}:${narrativeIndex}`}
                        label={`Narrative ${narrativeIndex + 1}`}
                        threads={groupCommentsByThread(items)}
                        reportOwnerUserId={reportOwnerUserId}
                        canResolve={canResolve}
                        onToggleResolved={onToggleResolved}
                      />
                    );
                  }
                  return (
                    <Box key={`${versionNumber}:${narrativeIndex}`}>
                      <Typography
                        variant="caption"
                        sx={{ fontWeight: 600, display: "block", mb: 0.5 }}
                      >
                        Narrative {narrativeIndex + 1}
                      </Typography>
                      <NarrativeWithInlineComments
                        narrativeIndex={narrativeIndex}
                        text={text}
                        comments={items}
                        mode="co-readonly"
                        reportOwnerUserId={reportOwnerUserId}
                      />
                    </Box>
                  );
                },
              )}
            </Stack>
          </Box>
        )}
        {general.length > 0 && (
          <CommentBucket
            label="General"
            threads={general}
            reportOwnerUserId={reportOwnerUserId}
            canResolve={canResolve}
            onToggleResolved={onToggleResolved}
          />
        )}
      </Stack>
    </Box>
  );
}

function CommentBucket({
  label,
  threads,
  reportOwnerUserId,
  canResolve,
  onToggleResolved,
}: {
  label: string;
  threads: { parent: CoReviewComment; replies: CoReviewComment[] }[];
  reportOwnerUserId?: string;
  canResolve: boolean;
  onToggleResolved: (commentId: string, nextResolved: boolean) => void;
}) {
  const theme = useTheme();
  // Replies aren't counted independently; only the top-level thread count.
  const totalCount = threads.length;
  return (
    <Box>
      <Typography variant="caption" sx={{ fontWeight: 700, color: "text.secondary" }}>
        {label} ({totalCount})
      </Typography>
      <Stack spacing={0.75} sx={{ mt: 0.5 }}>
        {threads.map(({ parent, replies }) => {
          const resolved = Boolean(parent.resolvedAt);
          const tint = resolved
            ? theme.palette.success.main
            : theme.palette.warning.main;
          return (
            <Box key={parent.commentId}>
              <Box
                sx={{
                  p: 1,
                  borderRadius: 1,
                  border: `1px solid ${theme.palette.divider}`,
                  bgcolor: alpha(tint, 0.04),
                }}
              >
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.25 }}>
                  <Typography variant="caption" sx={{ fontWeight: 700 }}>
                    {formatCommentAuthor(parent, reportOwnerUserId)}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontSize: "0.7rem" }}
                  >
                    · {commentAnchorLabel(parent)}
                  </Typography>
                  <Chip
                    size="small"
                    variant="outlined"
                    label={commentTypeLabel(parent)}
                    sx={{ height: 18, fontSize: "0.7rem" }}
                  />
                  {canResolve ? (
                    <Tooltip
                      title={resolved ? "Mark unresolved" : "Mark resolved"}
                    >
                      <IconButton
                        size="small"
                        onClick={() =>
                          onToggleResolved(parent.commentId, !resolved)
                        }
                        sx={{ ml: "auto", p: 0.25 }}
                      >
                        {resolved ? (
                          <CheckCircleIcon
                            fontSize="small"
                            sx={{ color: theme.palette.success.main }}
                          />
                        ) : (
                          <RadioButtonUncheckedIcon fontSize="small" />
                        )}
                      </IconButton>
                    </Tooltip>
                  ) : (
                    resolved && (
                      <Chip
                        size="small"
                        color="success"
                        variant="outlined"
                        label="resolved"
                        sx={{ height: 18, fontSize: "0.7rem" }}
                      />
                    )
                  )}
                </Stack>
                {parent.quotedText && (
                  <Typography
                    variant="caption"
                    sx={{
                      display: "block",
                      fontStyle: "italic",
                      color: "text.secondary",
                      borderLeft: `2px solid ${theme.palette.divider}`,
                      pl: 1,
                      mb: 0.5,
                    }}
                  >
                    “{parent.quotedText}”
                  </Typography>
                )}
                <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                  {parent.commentText}
                </Typography>
              </Box>
              {replies.length > 0 && (
                <Stack
                  spacing={0.5}
                  sx={{
                    mt: 0.5,
                    pl: 1.5,
                    borderLeft: `2px solid ${alpha(theme.palette.divider, 0.7)}`,
                  }}
                >
                  {replies.map((r) => (
                    <Box
                      key={r.commentId}
                      sx={{
                        p: 0.75,
                        borderRadius: 1,
                        border: `1px solid ${theme.palette.divider}`,
                        bgcolor: "background.paper",
                      }}
                    >
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.25 }}>
                        <Typography
                          variant="caption"
                          sx={{ fontWeight: 600, fontSize: "0.7rem" }}
                        >
                          {formatCommentAuthor(r, reportOwnerUserId)} replied
                        </Typography>
                      </Stack>
                      <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                        {r.commentText}
                      </Typography>
                    </Box>
                  ))}
                </Stack>
              )}
            </Box>
          );
        })}
      </Stack>
    </Box>
  );
}
